import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Upload } from "lucide-react";
import { toast } from "sonner";
import { getProducts, createProduct } from "@/integrations/supabase/products";
import { uploadProductImage } from "@/integrations/supabase/storage";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getProductCategories, createProductCategory } from "@/integrations/supabase/taxonomies";
import { AddCategoryDialog } from "@/components/AddCategoryDialog";
import { useAuth } from "@/components/AuthProvider";

interface NewProductModalProps {
  onProductCreated?: () => void;
  triggerText?: string;
  triggerVariant?: "default" | "outline" | "ghost" | "destructive" | "secondary";
}

export function NewProductModal({
  onProductCreated,
  triggerText = "Criar novo produto",
  triggerVariant = "outline"
}: NewProductModalProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const { empresa } = useAuth();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    name: "",
    category: "",
    description: "",
    unit_price: 0,
    work_hours: 0,
    materials: "",
    profit_margin: 0,
  });

  // Buscar categorias
  const { data: categoriesData = [], refetch: refetchCategories } = useQuery({
    queryKey: ["productCategories", empresa?.id],
    queryFn: async () => {
      if (!empresa?.id) return [];
      try {
        const cats = await getProductCategories(empresa.id);
        return cats;
      } catch (error) {
        console.error("Erro ao carregar categorias:", error);
        return [];
      }
    },
    enabled: !!empresa?.id && open,
  });

  const handleAddCategory = async (categoryName: string) => {
    if (!empresa?.id) {
      toast.error("Erro ao identificar empresa");
      return;
    }

    try {
      await createProductCategory(empresa.id, categoryName, "");
      await refetchCategories();
      toast.success("Categoria adicionada com sucesso!");
    } catch (error) {
      console.error("Erro ao adicionar categoria:", error);
      toast.error("Erro ao adicionar categoria");
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Arquivo muito grande (máx. 5MB)");
        return;
      }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Nome do produto é obrigatório");
      return;
    }

    if (!formData.category) {
      toast.error("Categoria é obrigatória");
      return;
    }

    if (formData.unit_price <= 0) {
      toast.error("Preço deve ser maior que zero");
      return;
    }

    if (!formData.materials.trim()) {
      toast.error("Materiais são obrigatórios");
      return;
    }

    setLoading(true);

    try {
      let imageUrl = null;

      if (selectedImage) {
        setIsUploadingImage(true);
        const uploadResult = await uploadProductImage(selectedImage);
        if (uploadResult.ok) {
          imageUrl = uploadResult.url;
        }
        setIsUploadingImage(false);
      }

      const result = await createProduct({
        name: formData.name,
        type: formData.category,
        description: formData.description,
        unit_price: formData.unit_price,
        work_hours: formData.work_hours,
        profit_margin: formData.profit_margin,
        materials: formData.materials.split(",").map((m) => m.trim()),
        image_url: imageUrl,
      });

      if (result.ok) {
        toast.success("Produto criado com sucesso!");

        // Recarregar lista de produtos
        await queryClient.invalidateQueries({ queryKey: ["products"] });

        // Callback
        onProductCreated?.();

        // Resetar formulário e fechar
        setFormData({
          name: "",
          category: "",
          description: "",
          unit_price: 0,
          work_hours: 0,
          materials: "",
          profit_margin: 0,
        });
        setSelectedImage(null);
        setImagePreview(null);
        setOpen(false);
      } else {
        toast.error(result.error || "Erro ao criar produto");
      }
    } catch (error) {
      console.error("Erro ao criar produto:", error);
      toast.error("Erro ao criar produto");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={triggerVariant} size="sm">
          <Plus className="w-4 h-4 mr-2" />
          {triggerText}
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Novo Produto</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 overflow-y-auto flex-1 pr-1">
          {/* Nome e Categoria */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nome do Produto <span className="text-red-500">*</span></Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Ex: Camiseta Polo Bordada"
                required
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label>Categoria <span className="text-red-500">*</span></Label>
                <AddCategoryDialog
                  onAddCategory={handleAddCategory}
                  categories={categoriesData.map(c => c.name)}
                  trigger={
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 p-0"
                      title="Adicionar nova categoria"
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                  }
                />
              </div>
              <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a categoria" />
                </SelectTrigger>
                <SelectContent>
                  {categoriesData.map(cat => (
                    <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Descrição */}
          <div className="space-y-2">
            <Label>Descrição <span className="text-gray-400">(opcional)</span></Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Descreva o produto..."
            />
          </div>

          {/* Preço, Tempo e Margem */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Preço Base <span className="text-red-500">*</span></Label>
              <Input
                type="number"
                step="0.01"
                value={formData.unit_price}
                onChange={(e) => setFormData({...formData, unit_price: Number(e.target.value)})}
                placeholder="0.00"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Tempo Estimado (horas) <span className="text-gray-400">(opcional)</span></Label>
              <Input
                type="number"
                step="0.5"
                value={formData.work_hours}
                onChange={(e) => setFormData({...formData, work_hours: Number(e.target.value)})}
                placeholder="0"
              />
            </div>

            <div className="space-y-2">
              <Label>Margem de Lucro (%) <span className="text-gray-400">(opcional)</span></Label>
              <Input
                type="number"
                step="0.01"
                value={formData.profit_margin}
                onChange={(e) => setFormData({...formData, profit_margin: Number(e.target.value)})}
                placeholder="0"
              />
            </div>
          </div>

          {/* Materiais */}
          <div className="space-y-2">
            <Label>Materiais (separados por vírgula) <span className="text-red-500">*</span></Label>
            <Input
              value={formData.materials}
              onChange={(e) => setFormData({...formData, materials: e.target.value})}
              placeholder="Ex: Tecido, Linha, Botões"
              required
            />
          </div>

          {/* Imagem */}
          <div className="space-y-2">
            <Label>Foto do Produto <span className="text-gray-400">(opcional)</span></Label>
            {imagePreview ? (
              <div className="relative w-full h-40 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 overflow-hidden">
                <img src={imagePreview} alt="Preview" className="w-full h-full object-contain" />
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-gray-400">
                <Label className="cursor-pointer">
                  <span className="text-sm text-gray-600">Clique para selecionar uma imagem</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </Label>
                <p className="text-xs text-gray-500 mt-2">JPG, PNG ou GIF (máx. 5MB)</p>
              </div>
            )}
          </div>

          {/* Botões */}
          <div className="flex gap-2 pt-4 border-t justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={loading || isUploadingImage}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading || isUploadingImage}
            >
              {isUploadingImage ? (
                <>
                  <Upload className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                "Criar Produto"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
